import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Markdown } from '../components/Markdown';
import { Orb } from '../components/Orb';
import { api, ApiError } from '../lib/api';
import { passLabel } from '../lib/runLabels';
import { agentStatusChip } from '../lib/agentStatus';
import type { Agent, AgentStatus, Cost, Run, Standup } from '@shared/types';

/** Shape of GET /agents/:id (see worker routes/agents.ts). */
interface AgentDetailData {
  agent: Agent;
  runs: Run[];
  costs: Cost[];
  standups: Standup[];
}

/** admin/kill only ever flips the agent between resting 'idle' and 'off'. */
interface KillResult {
  kill_switch: number;
  status: AgentStatus;
}

function money(n: number, dp = 2): string {
  return `$${(Number.isFinite(n) ? n : 0).toFixed(dp)}`;
}

function fmtDur(ms?: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** created_at is either 'YYYY-MM-DD HH:MM:SS' or ISO — a slice reads both as 'MM-DD HH:MM'. */
function fmtWhen(s: string): string {
  return s.slice(5, 16).replace('T', ' ');
}

function StatusChip({ agent }: { agent: Pick<Agent, 'status' | 'kill_switch'> }) {
  const c = agentStatusChip(agent);
  return (
    <span className={`chip ${c.cls}`} title={c.tip}>
      <i />
      {c.label}
    </span>
  );
}

function RunChip({ status }: { status: Run['status'] }) {
  if (status === 'ok') return (<span className="chip chip-mint"><i />OK</span>);
  if (status === 'error') return (<span className="chip chip-coral"><i />Error</span>);
  return (<span className="chip chip-ice live"><i />Running</span>);
}

/** One labelled markdown block — renders nothing when the field is empty. */
function Field({ label, md }: { label: string; md?: string | null }) {
  if (!md) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div className="t-label">{label}</div>
      <Markdown text={md} style={{ fontSize: 12.5, marginTop: 4, color: 'var(--text)' }} />
    </div>
  );
}

export default function AgentDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<AgentDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [killErr, setKillErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let alive = true;
    setLoading(true);
    setData(null);
    setNotFound(false);
    setErr(null);

    const load = () =>
      api
        .get<AgentDetailData>(`/agents/${id}`)
        .then((d) => {
          if (!alive) return;
          setData(d);
          setLoading(false);
        })
        .catch((e) => {
          if (!alive) return;
          setLoading(false);
          if (e instanceof ApiError && e.status === 404) setNotFound(true);
          else setErr('Could not load this agent. Try again in a moment.');
        });

    load();
    const t = setInterval(() => {
      if (!busy) load();
    }, 12000);
    return () => {
      alive = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function toggleKill() {
    if (!data) return;
    const killing = data.agent.kill_switch === 0;
    if (
      killing &&
      !window.confirm(
        `Pause ${data.agent.name}? Any tasks they haven't started yet will be cancelled, and they'll stop working until you resume them.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setKillErr(null);
    try {
      const res = await api.post<KillResult>('/admin/kill', {
        agent_id: data.agent.id,
        on: killing,
      });
      setData({
        ...data,
        agent: { ...data.agent, kill_switch: res.kill_switch, status: res.status },
      });
    } catch {
      setKillErr(`Couldn't reach ${data.agent.name} — nothing changed.`);
    } finally {
      setBusy(false);
    }
  }

  // --- loading / unknown-id / error --------------------------------------
  if (loading) {
    return (
      <Layout title="Agent">
        <p className="small">Loading…</p>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout title="Unknown agent">
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <Orb agent="emma" size="" />
          </div>
          <p className="dim" style={{ maxWidth: 380, margin: '0 auto 18px' }}>
            No one on the team answers to “{id}”. They might have logged off, or the link took a
            wrong turn.
          </p>
          <Link className="btn btn-line sm" to="/agents">
            Back to the team
          </Link>
        </div>
      </Layout>
    );
  }

  if (err || !data) {
    return (
      <Layout title="Agent">
        <div className="card">
          <p className="dim">{err ?? 'Something went sideways.'}</p>
        </div>
      </Layout>
    );
  }

  const { agent, runs, costs, standups } = data;
  const killed = agent.kill_switch === 1;

  // costs summary (worker returns the last 14 days).
  const costTotal = costs.reduce((s, c) => s + (c.usd ?? 0), 0);
  const byProvider = new Map<string, number>();
  for (const c of costs) byProvider.set(c.provider, (byProvider.get(c.provider) ?? 0) + (c.usd ?? 0));
  const providers = [...byProvider.entries()].sort((a, b) => b[1] - a[1]);

  const actions = (
    <>
      <Link className="btn btn-line sm" to="/agents">
        ← Team
      </Link>
      <Link className="btn btn-primary sm" to={`/brains/${agent.id}`}>
        Open brain
      </Link>
    </>
  );

  return (
    <Layout title={agent.name} actions={actions}>
      <div className="grid">
        {/* Hero */}
        <section className="card" style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
          <Orb agent={agent.id} size="" state={agent.status} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="t-label">Agent</div>
            <h2 style={{ margin: '2px 0 10px' }}>{agent.role}</h2>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusChip agent={agent} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <button
              className={`btn ${killed ? 'btn-mint' : 'btn-danger'} sm`}
              onClick={toggleKill}
              disabled={busy}
            >
              {busy ? 'Working…' : killed ? 'Resume agent' : 'Pause agent'}
            </button>
            {killErr && <span className="small down">{killErr}</span>}
          </div>
        </section>

        {/* Remit */}
        <section className="card">
          <div className="t-label">Remit</div>
          {agent.remit_md ? (
            <Markdown text={agent.remit_md} style={{ lineHeight: 1.65, marginTop: 10 }} />
          ) : (
            <p className="dim" style={{ marginTop: 10 }}>
              No remit written down yet — this teammate is still finding their voice.
            </p>
          )}
        </section>

        {/* Recent runs */}
        <section className="card">
          <div className="t-label">Recent runs</div>
          {runs.length === 0 ? (
            <p className="dim" style={{ marginTop: 10 }}>
              Nothing on the board yet. The first pass will land here.
            </p>
          ) : (
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table className="ledger">
                <thead>
                  <tr>
                    <th>Work</th>
                    <th>Status</th>
                    <th className="r">Duration</th>
                    <th className="r">Tokens</th>
                    <th className="r">Cost</th>
                    <th className="r">When</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id}>
                      <td title={r.pass}>{passLabel(r.pass)}</td>
                      <td><RunChip status={r.status} /></td>
                      <td className="r">{fmtDur(r.duration_ms)}</td>
                      <td className="r">{(r.tokens_in + r.tokens_out).toLocaleString()}</td>
                      <td className="r">{money(r.cost_usd, 4)}</td>
                      <td className="r">{fmtWhen(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Costs (small) */}
        <section className="card">
          <div className="stat">
            <div className="t-label">Spend · last 14 days</div>
            <div className="num">{money(costTotal)}</div>
          </div>
          {providers.length > 0 && (
            <div className="small" style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {providers.map(([provider, usd]) => (
                <span key={provider}>
                  {provider} <span className="mono" style={{ color: 'var(--text)' }}>{money(usd)}</span>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Standup history */}
        <section className="card">
          <div className="t-label">Standup history</div>
          {standups.length === 0 ? (
            <p className="dim" style={{ marginTop: 10 }}>
              No standups filed yet. When {agent.name} checks in, the log starts here.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
              {standups.map((s) => (
                <div
                  key={s.id}
                  style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}
                >
                  <div className="mono small" style={{ color: 'var(--dim)' }}>{s.date}</div>
                  <Field label="Yesterday" md={s.yesterday_md} />
                  <Field label="Today" md={s.today_md} />
                  <Field label="Wins" md={s.wins_md} />
                  <Field label="Struggles" md={s.struggles_md} />
                  <Field label="Blocked on founder" md={s.blocked_on_founder_md} />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
